/* Money Progress — auto-detected savings stories + this month's card spending, top of Banking. */
import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { CheckCircle2, PiggyBank, ShoppingBag, Pencil, Check, X, Sparkles, Flame } from "lucide-react";

function RenameInline({ g, onDone, onCancel }) {
  const [name, setName] = useState(g.name);
  const save = async (e) => {
    e.preventDefault();
    await api.patch("/insights/groups", { group_key: g.key, name });
    onDone();
  };
  const reset = async () => {
    await api.patch("/insights/groups", { group_key: g.key, name: null });
    onDone();
  };
  return (
    <form onSubmit={save} className="flex items-center gap-1.5" data-testid={`mp-rename-form-${g.key}`}>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoFocus
        className="flex-1 min-w-0 bg-white border border-sand rounded-md px-2 py-1 text-xs focus:outline-none focus:border-forest"
        data-testid={`mp-rename-input-${g.key}`} placeholder="Group name (same name merges groups)" />
      <button type="submit" className="text-forest hover:text-forest-deep" title="Save" data-testid={`mp-rename-save-${g.key}`}><Check className="w-3.5 h-3.5" /></button>
      {g.custom_named && <button type="button" onClick={reset} className="text-[10px] text-sage hover:text-ink" data-testid={`mp-rename-reset-${g.key}`}>reset</button>}
      <button type="button" onClick={onCancel} className="text-sage hover:text-ink" data-testid={`mp-rename-cancel-${g.key}`}><X className="w-3.5 h-3.5" /></button>
    </form>
  );
}

function SavingsStory({ g, onChanged }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className={`min-w-[240px] max-w-[300px] shrink-0 rounded-xl border p-4 ${g.paid ? "bg-forest text-white border-forest" : "bg-linen border-sand"}`}
         data-testid={`mp-savings-${g.key}`}>
      <div className="flex items-center justify-between gap-2">
        <div className={`inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-semibold ${g.paid ? "text-[#E8D8B5]" : "text-gold"}`}>
          {g.paid ? <CheckCircle2 className="w-3 h-3" /> : <PiggyBank className="w-3 h-3" />}
          {g.paid ? "Paid" : "Saving"} · {g.count} lock{g.count === 1 ? "" : "s"}
        </div>
        {g.streak_badge ? (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${g.paid ? "bg-cream text-clay" : "bg-clay text-white"}`}
                title={`Paid on time ${g.streak_months} months in a row`} data-testid={`mp-streak-${g.key}`}>
            <Flame className="w-3 h-3" /> {g.streak_months}-month streak
          </span>
        ) : g.streak_months > 0 ? (
          <span className={`text-[9px] ${g.paid ? "text-mint-mid" : "text-sage"}`} data-testid={`mp-streak-progress-${g.key}`}>
            on time {g.streak_months}/3 mo
          </span>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-2"><RenameInline g={g} onDone={() => { setEditing(false); onChanged(); }} onCancel={() => setEditing(false)} /></div>
      ) : (
        <div className="mt-1.5 flex items-start justify-between gap-2">
          <div className="font-serif text-lg leading-snug capitalize" data-testid={`mp-savings-name-${g.key}`}>{g.paid ? `Paid ${g.name}` : g.name}</div>
          <button onClick={() => setEditing(true)} className={`shrink-0 mt-1 ${g.paid ? "text-mint-mid hover:text-white" : "text-sage hover:text-ink"}`}
            title="Rename or merge this group" data-testid={`mp-rename-btn-${g.key}`}><Pencil className="w-3 h-3" /></button>
        </div>
      )}
      <div className={`font-mono text-2xl mt-1 tabular-nums ${g.paid ? "text-white" : "text-ink"}`} data-testid={`mp-savings-amount-${g.key}`}>
        {fmtMoney(g.paid ? g.total : g.saved)}
        {!g.paid && g.target > 0 && <span className={`text-xs ml-1 ${g.paid ? "text-mint-mid" : "text-sage"}`}>of {fmtMoney(g.target)}</span>}
      </div>
      <div className={`text-[10px] mt-1 ${g.paid ? "text-mint-mid" : "text-moss"}`} data-testid={`mp-savings-story-${g.key}`}>{g.story}</div>
    </div>
  );
}

function SpendingCard({ s }) {
  if (!s || s.categories.length === 0) {
    return (
      <div className="min-w-[240px] shrink-0 rounded-xl border border-dashed border-sand p-4 text-xs text-sage flex items-center gap-2" data-testid="mp-spending-empty">
        <ShoppingBag className="w-4 h-4" /> No card purchases yet this month.
      </div>
    );
  }
  const top = s.categories[0];
  const max = top.total || 1;
  return (
    <div className="min-w-[280px] max-w-[340px] shrink-0 rounded-xl bg-ink text-cream p-4" data-testid="mp-spending">
      <div className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-semibold text-gold">
        <ShoppingBag className="w-3 h-3" /> Spending card · {new Date(s.month + "-15").toLocaleDateString(undefined, { month: "long" })}
        {s.sample && <span className="ml-1 px-1.5 py-0.5 rounded bg-gold/20 text-gold normal-case tracking-normal" data-testid="mp-spending-sample">{s.preview ? "preview" : "sample"}</span>}
      </div>
      <div className="font-serif text-lg mt-1.5 leading-snug" data-testid="mp-spending-top">{top.label} · {fmtMoney(top.total)}</div>
      <div className="text-[10px] text-sage-light">{s.preview ? "Preview — switches to your real purchases after the first card swipe" : `${fmtMoney(s.total)} across ${s.categories.reduce((n, c) => n + c.count, 0)} purchases`}</div>
      <ul className="mt-3 space-y-1.5">
        {s.categories.slice(0, 4).map((c) => (
          <li key={c.label} className="text-[11px]" data-testid={`mp-cat-${c.key}`}>
            <div className="flex justify-between"><span>{c.label}</span><span className="font-mono">{fmtMoney(c.total)}</span></div>
            <div className="h-1 rounded-full bg-ink-soft mt-0.5"><div className="h-full rounded-full bg-gold" style={{ width: `${(c.total / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MoneyProgressStrip() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/insights/money-progress"); setData(data); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const hasAny = data.savings.length > 0 || (data.spending?.categories?.length || 0) > 0;
  return (
    <section className="mt-8" data-testid="money-progress-strip">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-sage"><Sparkles className="w-3 h-3 text-gold" /> Money progress</div>
          <h2 className="font-serif text-2xl tracking-tight mt-1" data-testid="money-progress-headline">
            {data.stories[0] || "Your money story starts with the first lock."}
          </h2>
        </div>
        <div className="text-[10px] text-sage">Locks with matching words are grouped automatically · click the pencil to rename or merge</div>
      </div>
      {hasAny ? (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="money-progress-cards">
          {data.savings.map((g) => <SavingsStory key={g.key} g={g} onChanged={load} />)}
          <SpendingCard s={data.spending} />
        </div>
      ) : (
        <div className="border border-dashed border-sand rounded-xl p-5 text-sm text-sage" data-testid="money-progress-empty">
          Create a time lock or bill lock and your progress will show up here.
        </div>
      )}
    </section>
  );
}
